import { yupResolver } from "@hookform/resolvers/yup";
import ChevronRight from "@mui/icons-material/ChevronRight";
import Close from "@mui/icons-material/Close";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import InfoOutlined from "@mui/icons-material/InfoOutlined";
import Style from "@mui/icons-material/Style";
import { Box, Button, IconButton, Skeleton, Stack, Typography } from "@mui/material";
import { useSetAtom } from "jotai";
import { countBy } from "lodash-es";
import { useCallback, useMemo, useState } from "react";
import { FormProvider, useForm, useWatch } from "react-hook-form";
import { createSearchParams, useNavigate, useSearchParams } from "react-router";
import { useToken } from "../../server/auth/use-token";
import { useFlag } from "../../server/optimizely";
import { AutoAwesomeColouredOutlined } from "../../styles/icons/auto-awesome-coloured-outlined";
import { useDialog } from "../../utils/hooks/use-dialog";
import yup from "../../utils/yup";
import { usePendo } from "../analytics/hooks/use-pendo-watch";
import { trackContentGeneration } from "../content-generation/track-content-generation";
import { useAccountLimitsContext } from "../context/account-limit-context";
import { GradientBackground } from "../generic/components/gradient-background";
import { PiiGuidanceNotice } from "../generic/components/pii-guidance-notice";
import { useMultiformat } from "../multiformat/hooks/use-multi-format";
import { contentGenerationIdAtom } from "../multiformat/store/content-generation-id-atom";
import { isLongform } from "../multiformat/store/types";
import { ContentDescriptionTextField } from "./content-description-textfield";
import { ContentToneSelect } from "./content-tone-select";
import { ContentTypeSelect } from "./content-type-select";
import { CreateContentLinks } from "./create-content-links";
import { useBrandToneOptions } from "./hooks/use-brand-tone-options";
import { ImageStyleSelector } from "./image-style-selector";
import { InspireMeButton } from "./inspire-me-button";
import { LongformDialog } from "./longform-prompt-dialog";
import { mapLongformParams } from "./map-longform-params";
import { PhotoTypeSelect } from "./photo-type-select";

import type { CreateContentRequest } from "../multiformat/store/types";
import type { LongFormParams } from "./map-longform-params";
import type { BoxProps } from "@mui/material";
import type { FC } from "react";

export const CreateContentCard: FC<{ onClose?: () => void } & BoxProps> = ({
  onClose,
  sx = [],
  ...props
}) => {
  const multiformatLoader = useMultiformat();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const brandToneOptions = useBrandToneOptions();
  const pendo = usePendo();
  const { enable_pii_guidance } = useAccountLimitsContext();
  const [prototypes, , prototypesLoaded] = useFlag("mfai_prototypes");
  const getToken = useToken();
  const setContentGenerationId = useSetAtom(contentGenerationIdAtom);

  const schema = useMemo(
    () =>
      yup.object({
        q: yup.string().required("Please describe the content you wish to create."),
        tone: yup
          .string()
          .oneOf(brandToneOptions.data?.map((option) => option.value) ?? [])
          .optional(),
        format: yup.string().optional(),
        image_style: yup.object({
          photo: yup.boolean().required(),
          illustration: yup.boolean().required(),
          icon: yup.boolean().required(),
          video: yup.boolean().required(),
        }),
        db: yup.string().optional(),
        file: yup
          .object({
            id: yup.string().optional(),
            name: yup.string().optional(),
          })
          .optional()
          .default(undefined),
      }),
    [brandToneOptions],
  );

  const methods = useForm({
    defaultValues: {
      q: searchParams.get("q") ?? searchParams.get("answer") ?? "",
      format: searchParams.get("format") ?? "Communication",
      tone: searchParams.get("tone") ?? undefined,
      image_style: searchParams.get("image_styles")
        ? {
            photo: searchParams.get("image_styles")?.includes("photo") ?? true,
            illustration: searchParams.get("image_styles")?.includes("illustration") ?? true,
            icon: searchParams.get("image_styles")?.includes("icon") ?? true,
            video: searchParams.get("image_styles")?.includes("video") ?? true,
          }
        : {
            photo: true,
            illustration: true,
            icon: true,
            video: true,
          },
      db: searchParams.get("db") ?? undefined,
      file: searchParams.get("file_id")
        ? {
            id: searchParams.get("file_id") ?? undefined,
            name: searchParams.get("file_name") ?? undefined,
          }
        : undefined,
    },
    resolver: yupResolver(schema),
  });

  const [format, q, file] = useWatch({ control: methods.control, name: ["format", "q", "file"] });

  const longformDialog = useDialog<LongFormParams>();

  const handleSubmit = useCallback(
    async (request: CreateContentRequest) => {
      if (!multiformatLoader.data || !format) return;

      const config = multiformatLoader.data.content_types.find((c) => c.name === format);
      const imageStyles =
        Object.entries(request?.image_style ?? {})
          .filter(([, value]) => value)
          .map(([key]) => key)
          .join(",") || null;

      const sharedParams = {
        format,
        ...(request.file && { file_id: request.file.id, file_name: request.file.name }),
        ...(imageStyles && { image_styles: imageStyles }),
        ...(request.db && { db: request.db }),
        ...(request.tone && { tone: request.tone }),
      };

      const token = await getToken();
      const toneName = brandToneOptions.data?.find((t) => t.value === request.tone)?.label ?? null;
      if (config?.type === "Communication") {
        pendo?.track("create-content-prompt", {
          prompt: request.q,
          ...sharedParams,
        });
        const pathname = "/generate-comms";
        const params = createSearchParams({
          q: request.q,
          ...sharedParams,
        });

        const contentGenerationId = await trackContentGeneration(token, {
          contentType: "moment",
          prompt: JSON.stringify({ prompt: request.q, tone: toneName }),
        });
        setContentGenerationId(contentGenerationId);
        void navigate({ pathname, search: params.toString() });
        return;
      }

      if (isLongform(format) && file === undefined && request.q.length < 500) {
        pendo?.track("create-content-prompt-too-short", {
          prompt: request.q,
          ...sharedParams,
        });
        longformDialog.handleOpen(sharedParams);
        return;
      }

      pendo?.track("create-content-prompt", {
        prompt: request.q,
        ...sharedParams,
      });
      const params = createSearchParams({
        answer: request.q,
        ...sharedParams,
      });

      const contentGenerationId = await trackContentGeneration(token, {
        contentType: "mfai",
        subType: format,
        prompt: JSON.stringify({ prompt: request.q, tone: toneName }),
      });
      setContentGenerationId(contentGenerationId);
      void navigate({ pathname: "/create-content/preview", search: params.toString() });

      onClose?.();
    },
    [
      multiformatLoader.data,
      format,
      getToken,
      file,
      pendo,
      navigate,
      onClose,
      longformDialog,
      setContentGenerationId,
      brandToneOptions.data,
    ],
  );

  const handleInspire = useCallback(
    (prompt: string) => {
      methods.setValue("q", prompt, { shouldDirty: true, shouldTouch: true });
    },
    [methods],
  );

  const handleSkip = useCallback(
    async (longFormParams?: LongFormParams) => {
      const params = createSearchParams({
        answer: q,
        format: format ?? "",
        ...mapLongformParams(longFormParams ?? {}),
      });
      const token = await getToken();
      const toneName =
        brandToneOptions.data?.find((t) => t.value === methods.getValues("tone"))?.label ?? null;
      const contentGenerationId = await trackContentGeneration(token, {
        contentType: "mfai",
        subType: format ?? "",
        prompt: JSON.stringify({ prompt: q, tone: toneName }),
      });
      setContentGenerationId(contentGenerationId);
      void navigate({ pathname: "/create-content/preview", search: params.toString() });
      longformDialog.handleClose();
    },
    [
      q,
      format,
      navigate,
      longformDialog,
      setContentGenerationId,
      getToken,
      brandToneOptions.data,
      methods,
    ],
  );

  const handleContinue = useCallback(
    async (prompt: string, longFormParams?: LongFormParams) => {
      const params = createSearchParams({
        answer: prompt,
        format: format ?? "",
        ...mapLongformParams(longFormParams ?? {}),
      });
      const token = await getToken();
      const toneName =
        brandToneOptions.data?.find((t) => t.value === methods.getValues("tone"))?.label ?? null;
      const contentGenerationId = await trackContentGeneration(token, {
        contentType: "mfai",
        subType: format ?? "",
        prompt: JSON.stringify({ prompt, tone: toneName }),
      });
      setContentGenerationId(contentGenerationId);
      void navigate({ pathname: "/create-content/preview", search: params.toString() });
      longformDialog.handleClose();
    },
    [
      format,
      navigate,
      longformDialog,
      setContentGenerationId,
      getToken,
      brandToneOptions.data,
      methods,
    ],
  );

  const imageStyleConfig = useMemo(() => {
    if (multiformatLoader.loading) return;
    return countBy(
      multiformatLoader.data?.content_types
        .find((c) => c.name === format)
        ?.layouts?.map((l) => l.images[0]?.type),
    );
  }, [multiformatLoader.data, multiformatLoader.loading, format]);

  const customToneEnabled = (
    multiformatType: string | undefined,
    queryFormat: string | undefined,
  ): boolean => {
    const noCustomToneTypes = new Set([
      "Landscape Logo",
      "Square Logo",
      "TV Display",
      "Certificate",
      "Video Call Background",
      "Horizontal Banner",
      "Meme",
    ]);

    return (
      !noCustomToneTypes.has(multiformatType ?? "") && !noCustomToneTypes.has(queryFormat ?? "")
    );
  };

  return (
    <FormProvider {...methods}>
      <form onSubmit={methods.handleSubmit(handleSubmit)}>
        <GradientBackground sx={[...(sx instanceof Array ? sx : [sx])]} {...props}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              gap: 1,
              p: 2,
            }}
          >
            <CreateContentLinks />
          </Box>
          <Box sx={{ px: 2, pb: 8 }}>
            {onClose && (
              <IconButton
                data-analytics-id="create-content-close"
                onClick={onClose}
                sx={(theme) => ({
                  position: "relative",
                  float: "right",
                  top: theme.spacing(-3),
                  right: theme.spacing(-1),
                  [theme.breakpoints.down("md")]: {
                    right: theme.spacing(2),
                  },
                })}
              >
                <Close data-testid="CloseIcon" />
              </IconButton>
            )}
            <Box
              sx={{
                width: "100%",
                maxWidth: "600px",
                mx: "auto",
                boxSizing: "border-box",
                minWidth: 0,
                flexShrink: 0,
              }}
            >
              <Stack
                direction="row"
                spacing={2}
                justifyContent="space-between"
                alignItems="center"
                sx={{ mb: 4 }}
              >
                <Stack direction="row" spacing={1} alignItems="center">
                  <AutoAwesomeColouredOutlined />
                  <Typography variant="h5" color="text.primary">
                    Generate with AI
                  </Typography>
                </Stack>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Button
                    href="/moments"
                    data-analytics-id="create-content-from-scratch"
                    size="small"
                    variant="text"
                    endIcon={<ChevronRight />}
                  >
                    Communication from Scratch
                  </Button>
                  <Button
                    href="/moments/newsletter"
                    data-analytics-id="create-content-newsletter"
                    size="small"
                    variant="text"
                    endIcon={<ChevronRight />}
                  >
                    Newsletter
                  </Button>
                </Stack>
              </Stack>
              <Box sx={{ mb: 2 }}>
                <ContentTypeSelect prototypes={prototypes} loaded={prototypesLoaded} />
              </Box>
              <Box sx={{ mb: 3 }}>
                {!multiformatLoader.data && <Skeleton height="60px" variant="rounded" />}
                {multiformatLoader.data &&
                  customToneEnabled(
                    multiformatLoader.data.content_types.find((c) => c.name === format)?.type,
                    format,
                  ) && <ContentToneSelect />}
              </Box>
              <Stack gap={0.5}>
                <Box
                  sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}
                >
                  <Box>
                    {enable_pii_guidance && <PiiGuidanceNotice analyticsId="create-content" />}
                  </Box>
                  <Box>
                    <InspireMeButton onInspire={handleInspire} format={format ?? ""} />
                  </Box>
                </Box>
                <ContentDescriptionTextField
                  format={format}
                  fileUpload={Boolean(
                    format &&
                    ![
                      "Square Logo",
                      "Landscape Logo",
                      "TV Display",
                      "Video Call Background",
                      "Certificate",
                      "Horizontal Banner",
                      "Meme",
                      "Social Post",
                    ].includes(format),
                  )}
                />
                {isLongform(format) && (
                  <Stack display="flex" alignItems="center" direction="row" gap={1}>
                    <InfoOutlined fontSize="small" color="action" />
                    <Typography variant="body2" color="text.primary">
                      Consider entering around 1,000 characters for the best results
                    </Typography>
                  </Stack>
                )}
              </Stack>
              <AdvancedOptions imageStyleConfig={imageStyleConfig} />
            </Box>
          </Box>
        </GradientBackground>
        {longformDialog.open && (
          <LongformDialog
            open={longformDialog.open}
            contentType={format ?? ""}
            prompt={q}
            onSkip={() => void handleSkip()}
            onContinue={(prompt) => void handleContinue(prompt, longformDialog.data)}
            onClose={longformDialog.handleClose}
          />
        )}
      </form>
    </FormProvider>
  );
};

const AdvancedOptions: FC<{ imageStyleConfig: Record<string, number> | undefined }> = ({
  imageStyleConfig,
}) => {
  const [open, setOpen] = useState(false);

  return (
    <Box sx={{ mt: 2 }}>
      <Box
        onClick={() => setOpen((prev) => !prev)}
        data-analytics-id="create-content-advanced-options"
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          cursor: "pointer",
          color: "primary.main",
          userSelect: "none",
        }}
      >
        <Typography
          variant="body2"
          fontWeight={500}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            borderRadius: 2,
            transition: "background 0.2s ease",
            "&:hover": { backgroundColor: "rgba(72,83,255,0.05)" },
          }}
        >
          <Style fontSize="small" color="primary" />
          Advanced style options
          {open ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
        </Typography>
      </Box>
      {open && (
        <>
          {imageStyleConfig && <ImageStyleSelector config={imageStyleConfig} />}
          <PhotoTypeSelect />
        </>
      )}
    </Box>
  );
};
